import { Action, Reducer } from "redux";
import { AppThunkAction, remoteActions, MODEL_IS_VALID } from ".";
import { DeepClone } from "../helperFunctions";
import { TextInputState } from "./interfaces";

export const PAYMENT_TYPE_CHANGED = "PAYMENT_TYPE_CHANGED";
export const PAYMENT_PERIOD_CHANGED = "PAYMENT_PERIOD_CHANGED";
export const BANK_CHANGED = "BANK_CHANGED";
export const BANK_CODE_INVALID = "BANK_CODE_INVALID";
export const BANK_CODE_VALID = "BANK_CODE_VALID";

export const BANK_ACCOUNT_OWNER_NAME_CHANGED = "BANK_ACCOUNT_OWNER_NAME_CHANGED";
export const BANK_ACCOUNT_OWNER_SSN_CHANGED = "BANK_ACCOUNT_OWNER_SSN_CHANGED";
export const BANK_ACCOUNT_NUMBER_CHANGED = "BANK_ACCOUNT_NUMBER_CHANGED";
export const BANK_ACCOUNT_CLEARING_NUMBER_CHANGED = "BANK_ACCOUNT_CLEARING_NUMBER_CHANGED";
export const BANK_NOT_ACCOUNT_OWNER_CHANGED = "BANK_NOT_ACCOUNT_OWNER_CHANGED";

export interface PaymentTypeState {
  selectedPaymentTypeCode?: string;
  selectedBankCode?: string;
  paymentTypes?: IPaymentOption[];
  showError?: boolean;
  bankRequiredErrorMessage?: string;
  sectionHeader?: string;
  inputs: {
    bankAccountOwnerName: TextInputState;
    bankAccountOwnerSSN: TextInputState;
    bankAccountNumber: TextInputState;
    bankAccountClearingNumber: TextInputState;
    bankAccountOwner: {
      notOwner: boolean;
      label?: string;
    };
    isValid: boolean;
  };
  digitalSigning?: {
    showMessage?: boolean;
    message?: string;
  };
}

export interface IOptionItem {
  Code: string;
  Name: string;
  IsSelected: boolean;
}

export interface IPaymentOption {
  code: string;
  selected: boolean;
  available: boolean;
  title?: string;
  description?: string;
  secondaryText?: string;
  bankList?: IOptionItem[];
  bankCode?: string;
  bankHelperText?: string;
  showError?: boolean;
  paymentPeriods?: IOptionItem[];
}

// for dev. purposes
const defaults = {
  selectedPaymentTypeCode: "1EF",
  selectedBankCode: "0",
  bankRequiredErrorMessage: "Vänligen välj din bank i listan.",
  paymentTypes: [
    {
      code: "01",
      selected: false,
      available: true,
      title: "Lease",
      description: "Information about leasing",
    },
    {
      code: "02",
      selected: true,
      available: true,
      title: "E-faktura",
      description: "Betala månadsvis med e-faktura",
      paymentPeriods: [
        {
          Code: "1EF",
          Name: "Hav",
          IsSelected: true,
        },
        {
          Code: "2EF",
          Name: "Hel",
          IsSelected: false,
        },
      ],
      bankList: [
        {
          Code: "0",
          Name: "Choose your bank",
          IsSelected: true,
        },
        {
          Code: "SB",
          Name: "SEB",
          IsSelected: false,
        },
        {
          Code: "HDB",
          Name: "Handelsbanken",
          IsSelected: false,
        },
        {
          Code: "SWE",
          Name: "Swedbank",
          IsSelected: false,
        },
      ],
    },
    {
      code: "03",
      selected: false,
      available: true,
      title: "Pappersfaktura",
      description: "Betala helår med pappersfaktura",
    },
  ],
  sectionHeader: "Välj betalsätt",
  inputs: {
    bankAccountOwnerName: {
      title: "bankAccountOwnerName",
      state: 0,
      stateDescriptions: ["Default", "NotSet", "Incorrect"],
      secureValue: "",
      value: "",
    },
    bankAccountOwnerSSN: {
      title: "bankAccountOwnerSSN",
      state: 0,
      stateDescriptions: ["Default", "NotSet", "Incorrect"],
      secureValue: "",
      value: "",
    },
    bankAccountNumber: {
      title: "bankAccountNumber",
      state: 0,
      stateDescriptions: ["Default", "NotSet", "Incorrect"],
      secureValue: "",
      value: "",
    },
    bankAccountClearingNumber: {
      title: "bankAccountClearingNumber",
      state: 0,
      stateDescriptions: ["Default", "NotSet", "Incorrect"],
      secureValue: "",
      value: "",
    },
    bankAccountOwner: {
      notOwner: false,
      label: "Autogirot tillhör en annan person",
    },
    isValid: false,
  },
} as PaymentTypeState;

export interface IPaymentActionData {
  paymentCode: string | undefined;
  bankCode: string | undefined;
}

export type PaymentAction = {
  type:
    | typeof PAYMENT_TYPE_CHANGED
    | typeof BANK_CHANGED
    | typeof BANK_CODE_VALID
    | typeof BANK_CODE_INVALID
    | typeof PAYMENT_PERIOD_CHANGED
    | typeof BANK_ACCOUNT_OWNER_NAME_CHANGED
    | typeof BANK_ACCOUNT_OWNER_SSN_CHANGED
    | typeof BANK_ACCOUNT_NUMBER_CHANGED
    | typeof BANK_ACCOUNT_CLEARING_NUMBER_CHANGED
    | typeof BANK_NOT_ACCOUNT_OWNER_CHANGED
    | typeof MODEL_IS_VALID;
  data: IPaymentActionData | string | boolean;
};

const accountOwnerNameRegEx: RegExp = /^.{1,}$/;
const accountOwnerSSNRegEx: RegExp = /^\s*(19)?(20)?\d{6}[- ]?\d{4}\s*$/;
const accountNumberRegEx: RegExp = /^[0-9]{7,10}$/;
const accountClearinNumberRegEx: RegExp = /^\d{4}$/;

const getInputState = (value: string, pattern: RegExp) => (value.length > 0 ? (pattern.test(value) ? 0 : 2) : 1);

const isValid = (state: PaymentTypeState): PaymentTypeState => {
  const stateClone = DeepClone(state);
  const autogiroInputsRequired = stateClone.selectedPaymentTypeCode?.includes("BGA");

  let accountOwnerNameState = 0;
  let accountOwnerSSNState = 0;
  let accountNumberState = 0;
  let accountClearingNumberState = 0;

  if (autogiroInputsRequired) {
    accountOwnerNameState = stateClone.inputs.bankAccountOwner.notOwner
      ? getInputState(stateClone.inputs.bankAccountOwnerName.value ?? "", accountOwnerNameRegEx)
      : 0;
    accountOwnerSSNState = stateClone.inputs.bankAccountOwner.notOwner
      ? getInputState(stateClone.inputs.bankAccountOwnerSSN.value ?? "", accountOwnerSSNRegEx)
      : 0;
    accountNumberState = getInputState(stateClone.inputs.bankAccountNumber.value ?? "", accountNumberRegEx);
    accountClearingNumberState = getInputState(stateClone.inputs.bankAccountClearingNumber.value ?? "", accountClearinNumberRegEx);
  }

  return {
    ...stateClone,
    inputs: {
      ...stateClone.inputs,
      bankAccountOwnerName: {
        ...stateClone.inputs.bankAccountOwnerName,
        state: accountOwnerNameState,
      },
      bankAccountOwnerSSN: {
        ...stateClone.inputs.bankAccountOwnerSSN,
        state: accountOwnerSSNState,
      },
      bankAccountNumber: {
        ...stateClone.inputs.bankAccountNumber,
        state: accountNumberState,
      },
      bankAccountClearingNumber: {
        ...stateClone.inputs.bankAccountClearingNumber,
        state: accountClearingNumberState,
      },
      isValid: accountOwnerNameState === 0 && accountOwnerSSNState === 0 && accountNumberState === 0 && accountClearingNumberState === 0,
    },
  };
};

export const reducer: Reducer<PaymentTypeState> = (state: PaymentTypeState = defaults, input: Action): PaymentTypeState => {
  const action = input as PaymentAction;

  switch (action?.type) {
    case PAYMENT_TYPE_CHANGED: {
      return handlePaymentTypeChange(state, (action.data as IPaymentActionData).paymentCode);
    }
    case PAYMENT_PERIOD_CHANGED: {
      return handlePeriodChange(state, (action.data as IPaymentActionData).paymentCode);
    }
    case BANK_CHANGED: {
      return hadleBankChange(state, (action.data as IPaymentActionData).bankCode);
    }
    case BANK_CODE_VALID: {
      if (state.showError === false) {
        return state;
      }

      return {
        ...state,
        showError: false,
      };
    }
    case BANK_CODE_INVALID: {
      if (state.showError === true) {
        return state;
      }

      return {
        ...state,
        showError: true,
      };
    }

    case BANK_ACCOUNT_OWNER_NAME_CHANGED: {
      const value = (action.data as string) || "";
      return {
        ...state,
        inputs: {
          ...state.inputs,
          bankAccountOwnerName: {
            ...state.inputs.bankAccountOwnerName,
            value: value,
            state: getInputState(value, accountOwnerNameRegEx),
          },
        },
      };
    }
    case BANK_ACCOUNT_OWNER_SSN_CHANGED: {
      const value = (action.data as string) || "";
      return {
        ...state,
        inputs: {
          ...state.inputs,
          bankAccountOwnerSSN: {
            ...state.inputs.bankAccountOwnerSSN,
            value: value,
            state: getInputState(value, accountOwnerSSNRegEx),
          },
        },
      };
    }
    case BANK_ACCOUNT_NUMBER_CHANGED: {
      const value = (action.data as string) || "";
      return {
        ...state,
        inputs: {
          ...state.inputs,
          bankAccountNumber: {
            ...state.inputs.bankAccountNumber,
            value: value,
            state: getInputState(value, accountNumberRegEx),
          },
        },
      };
    }
    case BANK_ACCOUNT_CLEARING_NUMBER_CHANGED: {
      const value = (action.data as string) || "";
      return {
        ...state,
        inputs: {
          ...state.inputs,
          bankAccountClearingNumber: {
            ...state.inputs.bankAccountClearingNumber,
            value: value,
            state: getInputState(value, accountClearinNumberRegEx),
          },
        },
      };
    }
    case BANK_NOT_ACCOUNT_OWNER_CHANGED: {
      return {
        ...state,
        inputs: {
          ...state.inputs,
          bankAccountOwner: {
            ...state.inputs.bankAccountOwner,
            notOwner: action.data as boolean,
          },
        },
      };
    }

    case MODEL_IS_VALID: {
      return isValid(state);
    }
    default:
      return state;
  }
};

function handlePaymentTypeChange(state: PaymentTypeState, selectedPaymentCode: string | undefined): PaymentTypeState {
  // Do not edit provided state directly! State should be immutable!
  // Clone state and do changes there!
  let stateClone = DeepClone(state);

  let bankCode = undefined;
  let finalSelectedPaymentCode = selectedPaymentCode;
  const paymentType = getPaymentType(stateClone, selectedPaymentCode);
  if (paymentType) {
    setPaymentTypeSelected(stateClone, paymentType);
    if (paymentType.paymentPeriods) {
      finalSelectedPaymentCode = paymentType.paymentPeriods.find(period => period.IsSelected)?.Code ?? finalSelectedPaymentCode;

      // pre-select period with current Payment code
      const period = paymentType.paymentPeriods.find(p => p.Code === finalSelectedPaymentCode);
      if (period) {
        period.IsSelected = true;
      }
    }
    bankCode = setBankCodeSelected(paymentType);

    // reset inputs when payment type changes, otherwise purchase fails
    stateClone.inputs.bankAccountClearingNumber.value = "";
    stateClone.inputs.bankAccountNumber.value = "";
    if (stateClone.showError && isBankCodeValid(bankCode)) {
      stateClone = {
        ...stateClone,
        showError: !isBankCodeValid(bankCode),
      };
    }
  }

  return {
    ...stateClone,
    selectedPaymentTypeCode: finalSelectedPaymentCode,
    selectedBankCode: bankCode,
  };
}

function hadleBankChange(state: PaymentTypeState, selectedBankCode: string | undefined): PaymentTypeState {
  let newState = DeepClone(state);
  const selectedPaymentCode = newState.selectedPaymentTypeCode;

  const paymentType = getPaymentType(newState, selectedPaymentCode);
  if (paymentType) {
    paymentType.bankCode = selectedBankCode;
  }

  if (state.selectedPaymentTypeCode === selectedPaymentCode) {
    newState = {
      ...newState,
      selectedBankCode: selectedBankCode,
      showError: !isBankCodeValid(selectedBankCode),
    };
  }
  return newState;
}

function handlePeriodChange(state: PaymentTypeState, code: string | undefined): PaymentTypeState {
  const stateClone = DeepClone(state);

  const paymentType = stateClone.paymentTypes?.find(type => type.paymentPeriods?.find(period => period.Code === code));
  if (paymentType && paymentType.paymentPeriods) {
    paymentType.paymentPeriods.forEach(x => {
      x.IsSelected = x.Code === code;
    });
    stateClone.selectedPaymentTypeCode = code;
  }

  return stateClone;
}

function getPaymentType(state?: PaymentTypeState, code?: string): IPaymentOption | undefined {
  return state?.paymentTypes?.find(type => type.paymentPeriods?.find(period => period.Code === code) || type.code === code);
}

function setPaymentTypeSelected(state: PaymentTypeState, selectedPaymentType: IPaymentOption) {
  selectedPaymentType.selected = true;
  //all others not selected
  state.paymentTypes?.filter(pType => pType.code !== selectedPaymentType.code).forEach(pType => (pType.selected = false));
}

function setBankCodeSelected(selectedPaymentType: IPaymentOption): string | undefined {
  let bankCode = selectedPaymentType.bankCode;
  if (!bankCode) {
    bankCode = selectedPaymentType.bankList?.find(i => i.IsSelected)?.Code;
    selectedPaymentType.bankCode = bankCode;
  }
  return bankCode;
}

export function validateSelectedPaymentType(paymentTypeState?: PaymentTypeState): boolean {
  let isSelectedBankCodeValid = true;
  const selectedPaymentTypeCode: string | undefined = paymentTypeState?.selectedPaymentTypeCode;
  if (selectedPaymentTypeCode) {
    const selectedPaymentType = getPaymentType(paymentTypeState, selectedPaymentTypeCode);
    if (selectedPaymentType?.bankList) {
      isSelectedBankCodeValid = isBankCodeValid(paymentTypeState?.selectedBankCode);
    }
  }
  return isSelectedBankCodeValid;
}

function isBankCodeValid(bankCode?: string): boolean {
  return bankCode && bankCode?.length > 0 && bankCode !== "0" ? true : false;
}

export const actions = {
  paymentTypeChanged:
    (value: IPaymentActionData): AppThunkAction<PaymentAction | {}> =>
    dispatch => {
      dispatch({
        type: PAYMENT_TYPE_CHANGED,
        data: value as IPaymentActionData,
      });
      dispatch(remoteActions.requestPriceData());
    },
  bankChanged:
    (value: unknown): AppThunkAction<PaymentAction | {}> =>
    dispatch => {
      dispatch({
        type: BANK_CHANGED,
        data: value as IPaymentActionData,
      });
    },
  paymentPeriodChanged:
    (value: IPaymentActionData): AppThunkAction<PaymentAction | {}> =>
    dispatch => {
      dispatch({
        type: PAYMENT_PERIOD_CHANGED,
        data: value as IPaymentActionData,
      });
      dispatch(remoteActions.requestPriceData());
    },

  accountOwnerNameChanged:
    (value: string): AppThunkAction<PaymentAction | {}> =>
    dispatch => {
      dispatch({
        type: BANK_ACCOUNT_OWNER_NAME_CHANGED,
        data: value as string,
      });
    },
  accountOwnerSSNChanged:
    (value: string): AppThunkAction<PaymentAction | {}> =>
    dispatch => {
      dispatch({
        type: BANK_ACCOUNT_OWNER_SSN_CHANGED,
        data: value as string,
      });
    },
  accountNumberChanged:
    (value: string): AppThunkAction<PaymentAction | {}> =>
    dispatch => {
      dispatch({
        type: BANK_ACCOUNT_NUMBER_CHANGED,
        data: value as string,
      });
    },
  accountClearingNumberChanged:
    (value: string): AppThunkAction<PaymentAction | {}> =>
    dispatch => {
      dispatch({
        type: BANK_ACCOUNT_CLEARING_NUMBER_CHANGED,
        data: value as string,
      });
    },
  notAccountOwnerChanged:
    (value: boolean): AppThunkAction<PaymentAction | {}> =>
    dispatch => {
      dispatch({
        type: BANK_NOT_ACCOUNT_OWNER_CHANGED,
        data: value as boolean,
      });
    },
};
